"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { AgentMessage, SessionInfo, SessionTreeNode } from "@/lib/types";
import { MessageView } from "./MessageView";
import { ChatInput, type AttachedImage, type ChatInputHandle } from "./ChatInput";
import { ChatMinimap, useMessageRefs } from "./ChatMinimap";
import { useAgentSession, type AgentPhase } from "@/hooks/useAgentSession";
import { useAudio } from "@/hooks/useAudio";
import { useDragDrop } from "@/hooks/useDragDrop";
import { getHiddenSkillToolCallIds, redactSkillText } from "@/lib/skill-redaction";

interface Props {
  session: SessionInfo | null;
  newSessionCwd: string | null;
  onAgentEnd?: () => void;
  onSessionCreated?: (session: SessionInfo) => void;
  onSessionForked?: (newSessionId: string) => void;
  onQuotaChanged?: () => void;
  modelsRefreshKey?: number;
  chatInputRef?: React.RefObject<ChatInputHandle | null>;
  onBranchDataChange?: (tree: SessionTreeNode[], activeLeafId: string | null, onLeafChange: (leafId: string | null) => void) => void;
  onSystemPromptChange?: (prompt: string | null) => void;
  onSessionStatsChange?: (stats: { tokens: { input: number; output: number; cacheRead: number; cacheWrite: number }; cost?: number } | null) => void;
  onContextUsageChange?: (usage: { percent: number | null; contextWindow: number; tokens: number | null } | null) => void;
  onOpenMembership?: () => void;
  remainingChats?: number;
  autoStartPrompt?: { id: string; prompt: string } | null;
  onAutoStartPromptConsumed?: (id: string) => void;
}

function phaseLabel(phase: AgentPhase): string {
  if (phase?.kind === "running_tools") {
    const names = phase.tools.map((t) => t.name);
    if (names.length === 0) return "正在运行工具...";
    if (names.length === 1) return `正在运行 ${names[0]}...`;
    if (names.length <= 3) return `正在运行 ${names.join(", ")}...`;
    return `正在运行 ${names.slice(0, 2).join(", ")}（另 ${names.length - 2} 个）...`;
  }
  if (phase?.kind === "waiting_model") return "等待模型响应...";
  return "思考中...";
}

const TYPEWRITER_PHRASES = [
  "看懂女生消息背后的意思。",
  "判断她是在试探、分享还是拒绝。",
  "帮你想清楚怎么回最合适。",
  "陪你把长期关系经营到稳定。",
  "帮你处理冷淡、冷战和争吵。",
  "帮你推进暧昧、恋爱到谈婚论嫁。",
  "帮你把话说得真诚、有边界。",
  "帮你判断这段关系下一步怎么走。",
];

const GUIDE_STEPS = [
  { title: "发来她的消息", desc: "文字、聊天截图、朋友圈内容都可以，先看她真正想表达什么。" },
  { title: "判断怎么回复", desc: "根据关系阶段、情绪信号和窗口强弱，给你更合适的回法。" },
  { title: "经营长期关系", desc: "处理冷淡、争吵、边界、安全感和推进节奏，目标是稳定走下去。" },
];

const GUIDE_ACTIONS = [
  { title: "她什么意思", prompt: "帮我分析女生这句话想表达什么，我应该怎么理解。我会把她的原话发给你。" },
  { title: "怎么回复她", prompt: "帮我判断这条消息怎么回更合适。我会发女生原话和我们现在的关系阶段。" },
  { title: "判断关系阶段", prompt: "帮我判断我们现在处在什么关系阶段，下一步最该做什么。我会补充我们的相处情况。" },
  { title: "争吵冷战修复", prompt: "我们刚吵完，现在有点冷战。帮我复盘问题、判断责任边界，并给我今天该怎么修复。" },
  { title: "表达需求", prompt: "我想表达自己的需求，但不想让她觉得我在指责。帮我组织一段真诚、有边界的话。" },
  { title: "谈婚论嫁规划", prompt: "我们开始考虑谈婚论嫁了，帮我梳理需要确认的关键问题：家庭、金钱、城市、孩子、边界和时间表。" },
  { title: "长期关系档案", prompt: "帮我初始化长期关系档案。请优先建立我的用户个人档案，记录我的基本情况、恋爱目标、沟通风格、关系需求、底线和当前困扰；再根据需要补充对方和关系信息。" },
];

const MATERIAL_HINTS = [
  "她的原话或聊天截图",
  "你们现在的关系阶段",
  "上一轮你怎么回的",
  "你想达到的结果",
];

const SCENARIO_PROMPTS = [
  { title: "朋友圈怎么发", prompt: "帮我设计一条适合发朋友圈的内容，目标是自然展示状态、不过度暴露需求感，并让女生有机会接话。我会补充我的近况和想传达的感觉。" },
  { title: "约会后怎么推进", prompt: "我们刚约会完，帮我判断这次约会反馈如何，以及接下来 24 小时内怎么发消息更自然。" },
  { title: "边界和需求表达", prompt: "我想表达自己的需求和边界，但不想显得指责或控制。帮我组织一段成熟、真诚、对方能听进去的话。" },
  { title: "谈婚论嫁问题", prompt: "我们已经到了谈婚论嫁阶段，帮我梳理需要沟通的关键问题，并设计一次不压迫对方的沟通方式。" },
];

function Typewriter({ phrases }: { phrases: string[] }) {
  const [phraseIdx, setPhraseIdx] = useState(() => Math.floor(Math.random() * phrases.length));
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [caretOn, setCaretOn] = useState(true);
  const currentPhrases = phrases.length > 0 ? phrases : TYPEWRITER_PHRASES;

  useEffect(() => {
    const blink = setInterval(() => setCaretOn((v) => !v), 530);
    return () => clearInterval(blink);
  }, []);

  useEffect(() => {
    setPhraseIdx((idx) => idx >= currentPhrases.length ? 0 : idx);
  }, [currentPhrases.length]);

  useEffect(() => {
    const current = currentPhrases[phraseIdx] ?? currentPhrases[0];
    let timeout: ReturnType<typeof setTimeout>;
    if (!deleting && text === current) {
      timeout = setTimeout(() => setDeleting(true), 1800);
    } else if (deleting && text === "") {
      setDeleting(false);
      setPhraseIdx((i) => (i + 1) % currentPhrases.length);
    } else {
      const next = deleting ? current.slice(0, text.length - 1) : current.slice(0, text.length + 1);
      timeout = setTimeout(() => setText(next), deleting ? 28 : 55);
    }
    return () => clearTimeout(timeout);
  }, [text, deleting, phraseIdx, currentPhrases]);

  return (
    <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
      {text}
      <span style={{ opacity: caretOn ? 1 : 0, color: "var(--accent)", marginLeft: 1 }}>▍</span>
    </span>
  );
}

function userMessageKey(message: AgentMessage): string | null {
  if (message.role !== "user") return null;
  const content = message.content;
  if (typeof content === "string") return redactSkillText(content).trim();
  if (!Array.isArray(content)) return String(content);
  return content
    .map((block) => {
      if (block.type === "text") return redactSkillText(block.text);
      if (block.type === "image") return "[image]";
      return JSON.stringify(block);
    })
    .join("\n")
    .trim();
}

export function ChatWindow({ session, newSessionCwd, onAgentEnd, onSessionCreated, onSessionForked, onQuotaChanged, modelsRefreshKey, chatInputRef, onBranchDataChange, onSystemPromptChange, onSessionStatsChange, onContextUsageChange, onOpenMembership, remainingChats, autoStartPrompt, onAutoStartPromptConsumed }: Props) {
  const [assistantDisplayName, setAssistantDisplayName] = useState("AI助手");
  const [welcomeGuidePhrases, setWelcomeGuidePhrases] = useState(TYPEWRITER_PHRASES);
  const [quotaModalMessage, setQuotaModalMessage] = useState<string | null>(null);
  const [dismissedSendError, setDismissedSendError] = useState<string | null>(null);
  const {
    loading, error, messages, entryIds, streamState,
    agentRunning, modelNames, modelList, modelThinkingLevels, modelThinkingLevelMaps, toolPreset, thinkingLevel,
    retryInfo, contextUsage, forkingEntryId,
    isCompacting, compactError, sendError, displayModel: displayModelValue, sessionStats,
    agentPhase,
    isNew,
    messagesEndRef, scrollContainerRef,
    lastUserMsgRef,
    handleSend, handleAbort, handleFork, handleNavigate, handleModelChange,
    handleCompact, handleSteer, handleFollowUp, handleAbortCompaction,
    handleToolPresetChange, handleThinkingLevelChange, handleAgentEventRef,
  } = useAgentSession({
    session, newSessionCwd, onAgentEnd, onSessionCreated, onSessionForked, onQuotaChanged,
    modelsRefreshKey, onBranchDataChange, onSystemPromptChange,
  });

  useEffect(() => {
    fetch("/api/app-settings")
      .then((res) => res.ok ? res.json() : null)
      .then((data: { assistantDisplayName?: string; welcomeGuidePhrases?: string[] } | null) => {
        if (data?.assistantDisplayName) setAssistantDisplayName(data.assistantDisplayName);
        if (Array.isArray(data?.welcomeGuidePhrases) && data.welcomeGuidePhrases.length > 0) {
          setWelcomeGuidePhrases(data.welcomeGuidePhrases);
        }
      })
      .catch(() => {});
  }, []);

  const { soundEnabled, onSoundToggle, playDoneSound } = useAudio();
  const playDoneSoundRef = useRef(playDoneSound);
  playDoneSoundRef.current = playDoneSound;
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;

  // Wrap agent event handler to play sound on agent_end
  const origHandler = handleAgentEventRef.current;
  useEffect(() => {
    handleAgentEventRef.current = (event) => {
      if (event.type === "agent_end" && soundEnabledRef.current) {
        playDoneSoundRef.current();
      }
      origHandler?.(event);
    };
  }, [origHandler, handleAgentEventRef]);

  // Push session stats up to AppShell for the top bar.
  // Compare scalar fields to avoid loops from new object identity each render.
  const statsKey = sessionStats
    ? `${sessionStats.tokens.input}|${sessionStats.tokens.output}|${sessionStats.tokens.cacheRead}|${sessionStats.tokens.cacheWrite}|${sessionStats.cost ?? 0}`
    : null;
  const sessionStatsRef = useRef(sessionStats);
  sessionStatsRef.current = sessionStats;
  useEffect(() => {
    onSessionStatsChange?.(sessionStatsRef.current);
  }, [statsKey, onSessionStatsChange]);
  useEffect(() => () => { onSessionStatsChange?.(null); }, [onSessionStatsChange]);

  // Push context usage up to AppShell as well.
  const ctxKey = contextUsage
    ? `${contextUsage.percent ?? "null"}|${contextUsage.contextWindow}|${contextUsage.tokens ?? "null"}`
    : null;
  const contextUsageRef = useRef(contextUsage);
  contextUsageRef.current = contextUsage;
  useEffect(() => {
    onContextUsageChange?.(contextUsageRef.current);
  }, [ctxKey, onContextUsageChange]);
  useEffect(() => () => { onContextUsageChange?.(null); }, [onContextUsageChange]);

  useEffect(() => {
    if (!sendError && !quotaModalMessage) setDismissedSendError(null);
  }, [sendError, quotaModalMessage]);

  const onDrop = useCallback((files: File[]) => {
    chatInputRef?.current?.addImages(files);
  }, [chatInputRef]);

  const { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop } = useDragDrop(onDrop);

  const visibleMessages = messages.filter((m) => m.role === "user" || m.role === "assistant");
  const visibleStreamingMessage = streamState.streamingMessage && streamState.streamingMessage.role !== "user"
    ? ({ ...streamState.streamingMessage, role: "assistant" } as Partial<AgentMessage>)
    : null;
  const messageRefs = useMessageRefs(visibleMessages.length);

  const isEmptyNew = isNew && messages.length === 0 && !streamState.isStreaming && !agentRunning;

  const availableThinkingLevels = displayModelValue
    ? (modelThinkingLevels[`${displayModelValue.provider}:${displayModelValue.modelId}`] ?? null)
    : null;

  const currentThinkingLevelMap = displayModelValue
    ? (modelThinkingLevelMaps[`${displayModelValue.provider}:${displayModelValue.modelId}`] ?? null)
    : null;
  const activeSendError = quotaModalMessage ?? sendError;

  const handleSendWithQuotaCheck = useCallback(async (message: string, images?: AttachedImage[]) => {
    if (remainingChats !== undefined && remainingChats <= 0) {
      setQuotaModalMessage("剩余对话次数不足，请联系管理员增加次数或开通会员。");
      onQuotaChanged?.();
      return false;
    }
    setQuotaModalMessage(null);
    return handleSend(message, images);
  }, [handleSend, onQuotaChanged, remainingChats]);

  const consumedAutoStartPromptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoStartPrompt || consumedAutoStartPromptRef.current === autoStartPrompt.id) return;
    if (loading || agentRunning) return;
    consumedAutoStartPromptRef.current = autoStartPrompt.id;
    const id = window.setTimeout(async () => {
      const ok = await handleSendWithQuotaCheck(autoStartPrompt.prompt);
      if (ok !== false) onAutoStartPromptConsumed?.(autoStartPrompt.id);
      else consumedAutoStartPromptRef.current = null;
    }, 0);
    return () => window.clearTimeout(id);
  }, [agentRunning, autoStartPrompt, handleSendWithQuotaCheck, loading, onAutoStartPromptConsumed]);

  const handleGuideExample = useCallback((example: string) => {
    chatInputRef?.current?.insertText(example);
  }, [chatInputRef]);

  const chatInputElement = (
    <ChatInput
      ref={chatInputRef}
      onSend={handleSendWithQuotaCheck}
      onAbort={handleAbort}
      onSteer={agentRunning ? handleSteer : undefined}
      onFollowUp={agentRunning ? handleFollowUp : undefined}
      isStreaming={agentRunning}
      model={displayModelValue}
      modelNames={modelNames}
      modelList={modelList}
      onModelChange={handleModelChange}
      onCompact={session || isNew ? handleCompact : undefined}
      onAbortCompaction={handleAbortCompaction}
      isCompacting={isCompacting}
      compactError={compactError}
      toolPreset={toolPreset}
      onToolPresetChange={session || isNew ? handleToolPresetChange : undefined}
      thinkingLevel={thinkingLevel}
      onThinkingLevelChange={session || isNew ? handleThinkingLevelChange : undefined}
      availableThinkingLevels={availableThinkingLevels}
      thinkingLevelMap={currentThinkingLevelMap}
      retryInfo={retryInfo}
      soundEnabled={soundEnabled}
      onSoundToggle={onSoundToggle}
    />
  );

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-text-muted">
        正在加载会话...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="love-drop-zone pointer-events-none absolute inset-0 z-50 flex animate-[drop-zone-in_0.15s_ease_both] items-center justify-center backdrop-blur-[1px]">
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            {[0, 0.8, 1.6].map((delay) => (
              <div
                key={delay}
                className="love-drop-ripple absolute h-[720px] w-[720px] rounded-full border-[1.5px] border-solid animate-[drop-ripple_2.4s_ease-out_infinite_backwards]"
                style={{ transformOrigin: "center", animationDelay: `${delay}s` }}
              />
            ))}
          </div>
          <svg
            width="280" height="280" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg"
            className="love-drop-illustration"
          >
            <rect x="28" y="44" width="84" height="60" rx="8" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.8"/>
            <path d="M36 100 L54 72 L68 88 L80 74 L104 100Z" fill="currentColor" fillOpacity="0.16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.4" strokeLinejoin="round"/>
            <circle cx="96" cy="58" r="8" fill="currentColor" fillOpacity="0.22" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.6"/>
            <g stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.4" strokeLinecap="round">
              <line x1="96" y1="46" x2="96" y2="43"/>
              <line x1="96" y1="70" x2="96" y2="73"/>
              <line x1="84" y1="58" x2="81" y2="58"/>
              <line x1="108" y1="58" x2="111" y2="58"/>
              <line x1="87.5" y1="49.5" x2="85.4" y2="47.4"/>
              <line x1="104.5" y1="66.5" x2="106.6" y2="68.6"/>
              <line x1="104.5" y1="49.5" x2="106.6" y2="47.4"/>
              <line x1="87.5" y1="66.5" x2="85.4" y2="68.6"/>
            </g>
          </svg>
        </div>
      )}

      {activeSendError && activeSendError !== dismissedSendError && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="send-error-title"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(15,23,42,0.28)",
            padding: 20,
          }}
          onClick={() => setDismissedSendError(activeSendError)}
        >
          <div
            style={{
              width: "min(420px, 100%)",
              background: "var(--bg-panel)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              boxShadow: "0 24px 60px rgba(15,23,42,0.22)",
              padding: 20,
              color: "var(--text)",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(239,68,68,0.10)",
                color: "#ef4444",
                flexShrink: 0,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </span>
              <h2 id="send-error-title" style={{ margin: 0, fontSize: 16, fontWeight: 650 }}>发送失败</h2>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: "var(--text-muted)", marginBottom: 18 }}>
              {activeSendError}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              {onOpenMembership && (
                <button
                  onClick={() => {
                    setDismissedSendError(activeSendError);
                    onOpenMembership();
                  }}
                  style={{
                    height: 34,
                    padding: "0 14px",
                    border: "1px solid var(--border)",
                    borderRadius: 7,
                    background: "var(--bg)",
                    color: "var(--text)",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  查看会员套餐
                </button>
              )}
              <button
                onClick={() => setDismissedSendError(activeSendError)}
                style={{
                  height: 34,
                  padding: "0 14px",
                  border: "none",
                  borderRadius: 7,
                  background: "var(--accent)",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}

      {isEmptyNew ? (
        <div className="new-chat-home love-coach-home flex flex-1 flex-col overflow-hidden">
          <div className="love-coach-home-scroll px-4">
            <div className="love-coach-home-inner">
              <section className="love-coach-hero">
                <div className="love-coach-hero-main">
                  <div className="love-coach-kicker">
                    <span aria-hidden="true">❤</span>
                    恋爱军师默认在线
                  </div>
                  <h1>
                    看懂她的消息，
                    <span>知道怎么回。</span>
                  </h1>
                  <p>
                    发来女生原话、聊天截图或你们的相处背景，我会帮你判断她真正想表达什么、关系处在哪一步，以及下一句怎么说更稳。
                  </p>
                  <div className="love-coach-typewriter">
                    <Typewriter phrases={welcomeGuidePhrases} />
                  </div>
                  <div className="love-coach-primary-actions">
                    {GUIDE_ACTIONS.slice(0, 3).map((action) => (
                      <button key={action.title} type="button" onClick={() => handleGuideExample(action.prompt)}>
                        {action.title}
                      </button>
                    ))}
                  </div>
                </div>
                <aside className="love-coach-brief">
                  <div className="love-coach-brief-head">
                    <span>先给我这些</span>
                    <strong>越具体，判断越准</strong>
                  </div>
                  <div className="love-coach-materials">
                    {MATERIAL_HINTS.map((hint, index) => (
                      <div key={hint}>
                        <span>{index + 1}</span>
                        {hint}
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => handleGuideExample("我想让你帮我分析女生消息。我们现在的关系是：；她刚刚说：；我上一句说：；我的目标是：")}>
                    按模板开始
                  </button>
                </aside>
              </section>

              <section className="love-coach-flow">
                <div>
                  <span>怎么用</span>
                  <h2>不是让你背话术，而是帮你判断局势</h2>
                </div>
                <div className="love-coach-flow-steps">
                  {GUIDE_STEPS.map((step, index) => (
                    <div key={step.title}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{step.title}</strong>
                      <p>{step.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="love-coach-scenarios">
                {SCENARIO_PROMPTS.map((scenario) => (
                  <button key={scenario.title} type="button" onClick={() => handleGuideExample(scenario.prompt)}>
                    {scenario.title}
                  </button>
                ))}
              </section>
            </div>
          </div>
          <div className="love-coach-home-input">
            {chatInputElement}
          </div>
        </div>
      ) : (
      <>
      <div className="relative flex flex-1 overflow-hidden">
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pt-4 [scrollbar-width:none]">
          <div className="mx-auto max-w-[820px] px-4">

            {(() => {
              const toolResultsMap = new Map<string, import("@/lib/types").ToolResultMessage>();
              const hiddenToolIds = new Set<string>();
              for (const msg of messages) {
                for (const id of getHiddenSkillToolCallIds(msg)) hiddenToolIds.add(id);
                if (msg.role === "toolResult") {
                  const toolResult = msg as import("@/lib/types").ToolResultMessage;
                  if (!hiddenToolIds.has(toolResult.toolCallId)) toolResultsMap.set(toolResult.toolCallId, toolResult);
                }
              }
              let lastUserIdx = -1;
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === "user") { lastUserIdx = i; break; }
              }
              let refIdx = 0;
              const renderedUserKeys = new Set<string>();
              return messages.map((msg, idx) => {
                const userKey = userMessageKey(msg);
                if (userKey && renderedUserKeys.has(userKey)) return null;
                if (userKey) renderedUserKeys.add(userKey);
                const prevAssistantEntryId =
                  msg.role === "user" && idx > 0 && messages[idx - 1].role === "assistant"
                    ? entryIds[idx - 1]
                    : undefined;
                const isVisible = msg.role === "user" || msg.role === "assistant";
                const currentRefIdx = isVisible ? refIdx++ : -1;
                let showTimestamp = false;
                if (msg.role === "assistant") {
                  showTimestamp = true;
                  for (let j = idx + 1; j < messages.length; j++) {
                    const r = messages[j].role;
                    if (r === "user") break;
                    if (r === "assistant") { showTimestamp = false; break; }
                  }
                  // Hide on the currently-streaming tail (the streaming bubble owns the live timestamp)
                  if (showTimestamp && streamState.isStreaming && idx === messages.length - 1) {
                    showTimestamp = false;
                  }
                }
                const view = (
                  <MessageView
                    key={idx}
                    message={msg}
                    toolResults={toolResultsMap}
                    modelNames={modelNames}
                    assistantDisplayName={assistantDisplayName}
                    entryId={entryIds[idx]}
                    onFork={agentRunning || isNew || (idx === 0 && msg.role === "user") ? undefined : handleFork}
                    forking={forkingEntryId === entryIds[idx]}
                    onNavigate={agentRunning ? undefined : handleNavigate}
                    prevAssistantEntryId={agentRunning ? undefined : prevAssistantEntryId}
                    onEditContent={(content) => chatInputRef?.current?.insertIfEmpty(content)}
                    showTimestamp={showTimestamp}
                    prevTimestamp={idx > 0 ? (messages[idx - 1] as import("@/lib/types").AgentMessage & { timestamp?: number }).timestamp : undefined}
                  />
                );
                if (!isVisible) return view;
                return (
                  <div key={idx} ref={(el) => {
                    messageRefs.current[currentRefIdx] = el;
                    if (idx === lastUserIdx) { (lastUserMsgRef as { current: HTMLDivElement | null }).current = el; }
                  }}>
                    {view}
                  </div>
                );
              });
            })()}

            {streamState.isStreaming && visibleStreamingMessage && (
              <MessageView message={visibleStreamingMessage as AgentMessage} isStreaming modelNames={modelNames} assistantDisplayName={assistantDisplayName} />
            )}

            {agentRunning && !visibleStreamingMessage && (
              <div className="py-2 text-[13px] text-text-muted">
                <span className="animate-[pulse_1.5s_infinite]">{phaseLabel(agentPhase)}</span>
              </div>
            )}

            {agentRunning && (
              <div style={{ height: scrollContainerRef.current ? scrollContainerRef.current.clientHeight : "80vh" }} />
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
        <ChatMinimap
          messages={messages}
          streamingMessage={visibleStreamingMessage}
          scrollContainer={scrollContainerRef}
          messageRefs={messageRefs}
        />
      </div>

      <div className="relative">
        {chatInputElement}
      </div>
      </>
      )}
    </div>
  );
}
